import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Check } from 'lucide-react'

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''

  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (countdown <= 0) return
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [countdown])

  const handleResend = async () => {
    setStatus('sending')
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })
      if (res.status === 429) {
        setStatus('error')
        setCountdown(60)
        return
      }
      setStatus('sent')
      setCountdown(60)
    } catch {
      setStatus('error')
    }
  }

  const isDisabled = status === 'sending' || countdown > 0

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl shadow-card p-10 w-full max-w-[440px] text-center">
        <div className="text-[3rem] mb-4">📬</div>
        <h1 className="m-0 mb-3 text-[1.4rem] font-bold text-text-heading">Check your inbox</h1>
        <p className="text-text-2 leading-[1.6] mb-7 text-[0.95rem]">
          We sent a confirmation link to{' '}
          {email ? <span className="font-semibold text-text-heading">{email}</span> : 'your email address'}.
          {' '}Click the link to activate your account.
        </p>

        {status === 'sent' && (
          <p className="text-accent text-sm mb-4 flex items-center justify-center gap-1">
            <Check size={14} strokeWidth={2.5} /> A new confirmation link has been sent.
          </p>
        )}

        <button
          className={`inline-block px-7 py-[0.7rem] border-none rounded text-[0.95rem] font-semibold mb-4 ${isDisabled ? 'bg-text-3 text-bg cursor-default' : 'bg-accent text-accent-contrast cursor-pointer'}`}
          onClick={() => void handleResend()}
          disabled={isDisabled}
        >
          {status === 'sending'
            ? 'Sending…'
            : countdown > 0
              ? `Resend in ${countdown}s`
              : 'Resend confirmation email'}
        </button>

        <br />
        <Link to="/login" className="text-text-3 text-sm no-underline hover:underline">Back to sign in</Link>
      </div>
    </div>
  )
}
